import apiClient from '../utils/apiClient';
import { prepareImageForUpload } from '../utils/imagePrep';
import { prepareReceiptForUpload } from '../utils/receiptPrep';
import { MEDIA_UPLOAD_STAGES } from '../utils/mediaUploadStages';

const uploadFormDataWithProgress = (url, formData, onProgress) => new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
            onProgress(Math.round((event.loaded / event.total) * 100));
        }
    });

    xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
            return;
        }
        reject(new Error('Failed to upload image to storage'));
    });

    xhr.addEventListener('error', () => {
        reject(new Error('Failed to upload image to storage'));
    });

    xhr.open('POST', url);
    xhr.send(formData);
});

class MediaService {
    async uploadImage({ purpose, restaurantId, dishId, file, onStageChange, onProgress }) {
        onStageChange?.(MEDIA_UPLOAD_STAGES.PREPARING);
        onProgress?.(0);

        const prepared = await prepareImageForUpload(file);

        const presignResponse = await apiClient.post('/media/presign', {
            purpose,
            restaurantId,
            dishId,
            contentType: prepared.type || 'image/webp',
            contentLength: prepared.size,
        });

        const presignData = presignResponse.data || presignResponse;
        const { url, fields, uploadId } = presignData;

        const formData = new FormData();
        Object.entries(fields || {}).forEach(([key, value]) => {
            formData.append(key, value);
        });
        formData.append('file', prepared, 'original.webp');

        onStageChange?.(MEDIA_UPLOAD_STAGES.UPLOADING);
        onProgress?.(0);

        await uploadFormDataWithProgress(url, formData, onProgress);

        onStageChange?.(MEDIA_UPLOAD_STAGES.PROCESSING);
        onProgress?.(100);

        const confirmResponse = await apiClient.post('/media/confirm', {
            purpose,
            restaurantId,
            dishId,
            uploadId,
        }, { timeout: 30000 }); // 30 seconds

        onStageChange?.(null);
        onProgress?.(0);

        return confirmResponse.data || confirmResponse;
    }

    async uploadReceipt({
        purpose,
        restaurantId,
        expenseId,
        file,
        onStageChange,
        onProgress,
    }) {
        onStageChange?.(MEDIA_UPLOAD_STAGES.PREPARING);
        onProgress?.(0);

        const prepared = await prepareReceiptForUpload(file);

        const presignResponse = await apiClient.post('/media/presign', {
            purpose,
            restaurantId,
            expenseId,
            contentType: prepared.type,
            contentLength: prepared.size,
            originalFilename: prepared.originalFilename,
        });

        const presignData = presignResponse.data || presignResponse;
        const { url, fields, uploadId } = presignData;

        const formData = new FormData();
        Object.entries(fields || {}).forEach(([key, value]) => {
            formData.append(key, value);
        });
        formData.append('file', prepared.file, prepared.file.name);

        onStageChange?.(MEDIA_UPLOAD_STAGES.UPLOADING);
        onProgress?.(0);

        await uploadFormDataWithProgress(url, formData, onProgress);

        onStageChange?.(MEDIA_UPLOAD_STAGES.PROCESSING);
        onProgress?.(100);

        const confirmResponse = await apiClient.post('/media/confirm', {
            purpose,
            restaurantId,
            expenseId,
            uploadId,
            contentType: prepared.type,
            originalFilename: prepared.originalFilename,
        }, { timeout: 30000 });

        onStageChange?.(null);
        onProgress?.(0);

        return confirmResponse.data || confirmResponse;
    }

    async deleteReceipt({ purpose, restaurantId, expenseId }) {
        const response = await apiClient.delete('/media', { purpose, restaurantId, expenseId });
        return response.data || response;
    }

    async deleteImage({ purpose, restaurantId, dishId }) {
        const response = await apiClient.delete('/media', { purpose, restaurantId, dishId });
        return response.data || response;
    }
}

const mediaService = new MediaService();
export default mediaService;
